import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDate } from '@/lib/localizedDateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CalendarCheck, 
  CalendarX, 
  CalendarClock, 
  CheckCircle2, 
  XCircle,
  Clock,
  Phone,
  DollarSign
} from 'lucide-react';
import { usePromisesDashboard, type PromiseWithStatus } from '@/hooks/finance/usePromisesDashboard';
import { RegisterContactModal } from './RegisterContactModal';

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

interface PromiseCardProps {
  promise: PromiseWithStatus;
  onContact: (promise: PromiseWithStatus) => void;
}

function PromiseCard({ promise, onContact }: PromiseCardProps) {
  const promiseDate = parseDate(promise.promised_date)!;
  const daysText = promise.days_since_promise === 0 
    ? 'Hoje' 
    : promise.days_since_promise > 0 
      ? `${promise.days_since_promise}d atraso`
      : `em ${Math.abs(promise.days_since_promise)}d`;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium truncate">{promise.client_name}</span>
              {promise.promise_status === 'fulfilled' && (
                <Badge variant="default" className="bg-green-500 shrink-0">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Pagou
                </Badge>
              )}
              {promise.promise_status === 'broken' && (
                <Badge variant="destructive" className="shrink-0">
                  <XCircle className="h-3 w-3 mr-1" />
                  Não pagou
                </Badge>
              )}
              {promise.promise_status === 'pending' && (
                <Badge variant="secondary" className="shrink-0">
                  <Clock className="h-3 w-3 mr-1" />
                  Aguardando
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <CalendarCheck className="h-3 w-3" />
                {format(promiseDate, "dd/MM/yyyy", { locale: ptBR })}
              </span>
              <span className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                {formatCurrency(promise.promised_amount || promise.payment_amount, promise.payment_currency)}
              </span>
            </div>

            {promise.notes && (
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                {promise.notes}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className={`text-sm font-medium ${
              promise.days_since_promise > 0 ? 'text-destructive' : 'text-muted-foreground'
            }`}>
              {daysText}
            </span>
            
            {promise.promise_status !== 'fulfilled' && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onContact(promise)}
              >
                <Phone className="h-3 w-3 mr-1" />
                Contatar
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function PromisesTab() {
  const { data, isLoading } = usePromisesDashboard();
  const [selectedPromise, setSelectedPromise] = useState<PromiseWithStatus | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Para Hoje</CardTitle>
            <CalendarCheck className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalToday || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary?.amountToday || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vencidas</CardTitle>
            <CalendarX className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{summary?.totalOverdue || 0}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary?.amountOverdue || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Futuras</CardTitle>
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalFuture || 0}</div>
            <p className="text-xs text-muted-foreground">
              promessas agendadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cumpridas</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary?.totalFulfilled || 0}</div>
            <p className="text-xs text-muted-foreground">
              promessas pagas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Promises Tabs */}
      <Tabs defaultValue="today" className="w-full">
        <TabsList>
          <TabsTrigger value="today" className="gap-2">
            <CalendarCheck className="h-4 w-4" />
            Hoje ({data?.today.length || 0})
          </TabsTrigger>
          <TabsTrigger value="overdue" className="gap-2">
            <CalendarX className="h-4 w-4" />
            Vencidas ({data?.overdue.length || 0})
          </TabsTrigger>
          <TabsTrigger value="future" className="gap-2">
            <CalendarClock className="h-4 w-4" />
            Futuras ({data?.future.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-4">
          {data?.today.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nenhuma promessa para hoje.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {data?.today.map(promise => (
                <PromiseCard 
                  key={promise.id} 
                  promise={promise} 
                  onContact={setSelectedPromise}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="overdue" className="mt-4">
          {data?.overdue.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nenhuma promessa vencida. Excelente!
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {data?.overdue.map(promise => (
                <PromiseCard 
                  key={promise.id} 
                  promise={promise} 
                  onContact={setSelectedPromise}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="future" className="mt-4">
          {data?.future.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nenhuma promessa futura agendada.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {data?.future.map(promise => (
                <PromiseCard 
                  key={promise.id} 
                  promise={promise} 
                  onContact={setSelectedPromise}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Contact Modal */}
      {selectedPromise && (
        <RegisterContactModal
          open={!!selectedPromise}
          onClose={() => setSelectedPromise(null)}
          paymentId={selectedPromise.payment_id}
          contactId={selectedPromise.contact_id}
          clientName={selectedPromise.client_name}
        />
      )}
    </div>
  );
}
